import { useCallback, useMemo, useState } from "react";

import { useModal } from "../../hooks/useModal";
import { useStores } from "../../context/StoresContext";
import { useProducts } from "../../context/ProductsContext";
import { useProductOperations } from "../../hooks/inventario/useProductOperations";

import { CustomDataTable } from "../Tables/CustomDataTable";
import { InventarioAssignStores } from "./InventarioAssignStores";
import { InventarioConfirmUnassignStore } from "./InventarioConfirmUnassignStore";

import { icons } from "../../const/icons";

import "./InventarioContentStore.css";

/**
 * Muestra tabla de productos con sus almacenes asignados
 * y panel lateral con detalles del producto seleccionado
 */
export const InventarioContentStore = () => {
  const { selectedStore, selectedStoreData } = useStores();

  const { filteredProducts, pagination, isSearchMode, searchTerm } = useProducts();

  const { operations: productOperations, states: productStates } = useProductOperations();

  const { isOpen, open, close } = useModal();

  //*  ESTADOS LOCALES
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productToUnassign, setProductToUnassign] = useState(null);

  //* REFETCH
  const refetchProductsForCurrentStore = useCallback(
    async (page = pagination.pageNumber, size = pagination.pageSize) => {
      if (!selectedStoreData && selectedStore !== "Todos") return;

      try {
        if (selectedStore === "Todos") {
          await productOperations.fetchProducts({ page, size });
        } else {
          await productOperations.fetchProductsByStore(selectedStoreData.id, {
            page,
            size,
          });
        }
      } catch (error) {
        console.error("❌ Error al refrescar productos:", error);
      }
    },
    [
      selectedStore,
      selectedStoreData,
      productOperations,
      pagination.pageNumber,
      pagination.pageSize,
    ]
  );

  //*  HANDLERS
  const handleTablePageChange = async (page, pageSize) => {
    const backendPage = page - 1;

    if (isSearchMode && searchTerm) {
      await productOperations.searchProducts(searchTerm, { page: backendPage, size: pageSize });
    } else {
      await refetchProductsForCurrentStore(backendPage, pageSize);
    }
  };

  /**
   * Maneja la selección de un producto en la tabla
   */
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  /**
   * Abre el modal de asignación de almacenes
   */
  const handleOpenAssignModal = (product) => {
    setSelectedProduct(product);
    open();
  };

  /**
   * Abre modal de confirmación para eliminar asignación
   */
  const handleOpenUnassign = (e, product) => {
    e.stopPropagation();
    setProductToUnassign(product);
  };

  /**
   * Confirmar y ejecuta la eliminación de la asignación
   */
  const handleConfirmUnassign = async () => {
    if (!productToUnassign) return;

    try {
      // Remover el almacén del producto
      const result = await productOperations.dissociateProductFromStore(
        productToUnassign.id,
        selectedStoreData.id
      );

      if (result.success) {
        await refetchProductsForCurrentStore();
        setProductToUnassign(null);
      }

      return result;
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error("❌ Error al eliminar la asignación del almacén:", error);

      return {
        success: false,
        error: error.message || "Error al eliminar la asignación del almacén",
      };
    }
  };

  /**
   * Cancelar la eliminación
   */
  const handleCancelUnassign = () => {
    setProductToUnassign(null);
  };

  /**
   * Guarda la asignación de almacenes
   */
  const handleAssignStores = async (storesIds) => {
    if (!selectedProduct) {
      /* eslint-disable-next-line no-console */
      console.error("No hay producto seleccionado");
      return;
    }

    try {
      const result = await productOperations.associateProductToStore(selectedProduct.id, storesIds);

      if (result.success) {
        await refetchProductsForCurrentStore();

        close();
      } else {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error("❌ Error al asignar almacenes:", error);
      return {
        success: false,
        error: error.message || "Error al asignar almacenes",
      };
    }
  };

  //*  DEFINICIÓN DE COLUMNAS
  /**
   * Configuración de columnas de la tabla
   * Usa useMemo para evitar recrear en cada render
   */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Ítem",
        dataIndex: "sku",
        key: "sku",
        width: "10%",
        sorter: (a, b) => a.sku.localeCompare(b.sku),
        render: (text) => <span className="item-sku-cell">{text}</span>,
        onCell: () => ({
          style: { borderRight: "2px dashed #d1d5db" },
        }),
      },
      {
        title: "Producto",
        dataIndex: "product",
        key: "product",
        width: "25%",
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Almacenes",
        dataIndex: "almacenes",
        key: "almacenes",
        width: "35%",
        render: (stores) => {
          if (stores.length === 0) {
            return <span className="no-almacenes">Este ítem no está asignado a un almacén</span>;
          }

          return (
            <div className="almacenes-list">
              {stores.map((s, idx) => (
                <span key={`${s.id}-${idx}`} className="almacen-tag">
                  {s.name}

                  {idx < stores.length - 1 && <span className="almacen-separator"> | </span>}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        title: "Agregar a almacén",
        key: "assign",
        width: "15%",
        align: "center",
        render: (_, record) => (
          <button
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAssignModal(record);
            }}
            aria-label={`Agregar ${record.nombre} a otro almacén`}
          >
            <img src={icons.add} alt="Agregar a otro almacén" />
          </button>
        ),
      },
    ];

    const filteredColumns = [
      {
        title: "Ítem",
        dataIndex: "sku",
        key: "sku",
        width: "10%",
        sorter: (a, b) => a.sku.localeCompare(b.sku),
        render: (text) => <span className="item-sku-cell">{text}</span>,
      },
      {
        title: "Producto",
        dataIndex: "product",
        key: "product",
        width: "25%",
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Otros almacenes",
        dataIndex: "almacenes",
        key: "almacenes",
        width: "35%",
        render: (almacenes) => {
          // Filtrar el almacén actual si no es "Todos"
          const otrosAlmacenes =
            selectedStore === "Todos"
              ? almacenes
              : almacenes.filter((s) => s.name !== selectedStore);

          if (otrosAlmacenes.length === 0) {
            return null;
          }

          return (
            <div className="almacenes-list">
              {otrosAlmacenes.map((s, idx) => (
                <span key={`${s.id}-${idx}`} className="almacen-tag">
                  {s.name}
                  {idx < otrosAlmacenes.length - 1 && (
                    <span className="almacen-separator"> | </span>
                  )}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        title: "Eliminar de almacén actual",
        key: "unassign",
        width: "15%",
        align: "center",
        render: (_, record) => (
          <button
            className="action-button"
            onClick={(e) => handleOpenUnassign(e, record)}
            aria-label={`Eliminar ${record.nombre} de ${selectedStore}`}
          >
            <img src={icons.delete} alt="Eliminar de almacén actual" />
          </button>
        ),
      },
      {
        title: "Agregar a otro almacén",
        key: "assign",
        width: "15%",
        align: "center",
        render: (_, record) => (
          <button
            className="action-button "
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAssignModal(record);
            }}
            aria-label={`Agregar ${record.nombre} a otro almacén`}
          >
            <img src={icons.add} alt="Agregar a otro almacén" />
          </button>
        ),
      },
    ];

    return selectedStore === "Todos" ? baseColumns : filteredColumns;
  }, [selectedStore]);

  //  RENDER
  return (
    <>
      <div className="inventario-content-store-wrapper">
        {/* <div className="store-table-section-full"> */}
        <div className="inventario-table-container">
          <CustomDataTable
            data={filteredProducts}
            columns={columns}
            loading={productStates.fetchState.loading}
            onRowClick={handleSelectProduct}
            selectedRow={selectedProduct}
            showPagination={true}
            pagination={{
              current: pagination.pageNumber + 1,
              pageSize: pagination.pageSize,
              total: pagination.totalElements,
            }}
            onPageChange={handleTablePageChange}
            emptyText="No se encontraron productos"
            className="store-products-table"
          />
        </div>
      </div>

      {/* Modal de asignación de almacenes */}
      <InventarioAssignStores
        visible={isOpen}
        onClose={close}
        onSubmit={handleAssignStores}
        producto={selectedProduct}
        saving={productStates.associateState.loading}
      />

      {/* Modal de confirmación para eliminar la asignación de un producto a un almacén. */}
      <InventarioConfirmUnassignStore
        visible={!!productToUnassign}
        product={productToUnassign}
        almacen={selectedStoreData}
        onConfirm={handleConfirmUnassign}
        onCancel={handleCancelUnassign}
        saving={productStates.dissociateState.loading}
      />
    </>
  );
};
